import { Currency, Widget } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import React, { useCallback, useEffect, useState } from 'react';
import BudgetTooltip from '../components/BudgetTooltip';
import ForecastTooltip from '../components/ForecastTooltip';
import PastDueTooltip from '../components/PastDueTooltip';
import PlannedBudgetTooltip from '../components/PlannedBudgetTooltip';
import {
  Box,
  Boxes,
  ChartContainer,
  Label,
  LowerBoxes,
  Metric,
  SmallBox,
  UpperBox,
  Value,
  ValueContainer,
} from '../components/StyledComponents';
import ViewNav from '../components/ViewNav';
import TotalRevenueByMonthChart from './TotalRevenueByMonthChart';
import TotalRevenueProgressChart from './TotalRevenueProgressChart';

export default function TotalRevenueWidget({ project, view, actualsThrough, approvalStatuses, onViewChange }) {
  const { workspace } = useWorkspace();

  const api = useApi();
  const [{ data, isReady }, setQuery] = useState({ data: null, isReady: false });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .dashboard()
        .progress.totalRevenue({
          actualsThrough,
          approvalStatusId: approvalStatuses?.map(({ id }) => id),
        });
      setQuery({ data, isReady: true });
    } catch (error) {
      setQuery({ data: null, isReady: true });
    }
  }, [workspace.id, project.id, api, actualsThrough, approvalStatuses]);

  useEffect(() => {
    fetchData();
  }, [fetchData, project]);

  const boxes = {
    budget: !!data?.budget,
    forecast: data?.forecast?.dates.length > 0,
    get any() {
      return this.budget || this.forecast;
    },
  };

  const currency = project.currency;

  return (
    <Widget loading={!isReady}>
      <Widget.Preview style={{ minHeight: '32.4rem' }} />
      <Widget.Content>
        <ViewNav view={view} onViewChange={onViewChange} />

        {data && (
          <>
            <ChartContainer>
              {data &&
                {
                  progress: () => <TotalRevenueProgressChart project={project} data={data} />,
                  month: () => <TotalRevenueByMonthChart project={project} data={data} />,
                }[view]()}
            </ChartContainer>

            <Boxes>
              {project.permissions.viewRevenue && project.isBillable && (
                <Box>
                  <UpperBox>
                    <ValueContainer>
                      <Value>
                        <Currency
                          value={data.actual.revenue}
                          currency={currency}
                          minimumFractionDigits={0}
                          maximumFractionDigits={0}
                        />
                      </Value>
                      {project.revenueRecognitionMethod === 'invoiced' &&
                        ['fixed', 'fixed_recurring'].includes(project.billingTypeId) && (
                          <PastDueTooltip value={data.pastDue} currency={project.currency} />
                        )}
                    </ValueContainer>
                    <Label>Actual Total Revenue</Label>
                  </UpperBox>

                  {boxes.any && (
                    <LowerBoxes>
                      {boxes.budget && (
                        <SmallBox>
                          <Metric>
                            <Value>
                              <Currency
                                value={data.budget.revenue.estimated ?? 0}
                                currency={currency}
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                              />
                            </Value>
                            <Label>Budget</Label>
                          </Metric>
                          <Metric status={data.budget.revenue.status}>
                            <BudgetTooltip value={data.budget.revenue.percent}>
                              <Value>
                                <Currency
                                  value={data.budget.revenue.remaining}
                                  currency={currency}
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={0}
                                />
                              </Value>
                              <Label>Budget Left</Label>
                            </BudgetTooltip>
                          </Metric>
                        </SmallBox>
                      )}

                      {boxes.forecast && (
                        <SmallBox>
                          <Metric>
                            <ForecastTooltip
                              actual={data.actual.revenue}
                              forecast={data.forecast.revenue}
                              currency={currency}
                              metric="revenue">
                              <Value>
                                <Currency
                                  value={data.forecast.revenue.forecasted}
                                  currency={currency}
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={0}
                                />
                              </Value>
                              <Label>Forecast (EAC)</Label>
                            </ForecastTooltip>
                          </Metric>
                          <Metric status={data.forecast.revenue.status}>
                            <PlannedBudgetTooltip
                              value={data.forecast.revenue}
                              currency={currency}
                              metric="total revenue">
                              <Value>
                                <Currency
                                  value={data.forecast.revenue.left}
                                  currency={currency}
                                  minimumFractionDigits={0}
                                  maximumFractionDigits={0}
                                />
                              </Value>
                              <Label>Plan Left (ETC)</Label>
                            </PlannedBudgetTooltip>
                          </Metric>
                        </SmallBox>
                      )}
                    </LowerBoxes>
                  )}
                </Box>
              )}
            </Boxes>
          </>
        )}
      </Widget.Content>
    </Widget>
  );
}
